import jsPDF from 'jspdf';
import { BasePDFOptions } from '..';

type GroupedDailyRow = {
  transaction_count: number;
  revenue_type: 'income' | 'donation' | string;
  income_category_id: number;
  income_category_name: string;
  total_amount: number;
  total_tax: number;
  total_sub_total: number;
};

const formatCurrency = (amount: number | undefined): string => {
  const val = amount ?? 0;
  return new Intl.NumberFormat('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
};

export async function dailyCashReportTemplate(
  options: BasePDFOptions<GroupedDailyRow[]>
): Promise<void> {
  const {
    data,
    filename = 'daily-cash-report.pdf',
    title = 'Daily Cash Report',
    orientation = 'portrait',
    summary
  } = options;

  const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let y = margin;

  // Header
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, 22, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text(title, margin, 12);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  const now = new Date();
  pdf.text(now.toLocaleString('en-US'), pageWidth - margin, 12, { align: 'right' });

  y = 28;

  // Summary cards (use revenue_summary from API response, fallback to manual calculation)
  const revenueSummary = (summary as any)?.revenue_summary;
  const incomeTotal = revenueSummary?.income_sales ?? data.filter(r => r.revenue_type === 'income').reduce((s, r) => s + (r.total_amount || 0), 0);
  const donationTotal = revenueSummary?.donation_sales ?? data.filter(r => r.revenue_type === 'donation').reduce((s, r) => s + (r.total_amount || 0), 0);
  const totalSales = revenueSummary?.total_sales ?? (incomeTotal + donationTotal);

  // Section background
  pdf.setFillColor(248, 250, 252); // slate-50
  pdf.rect(0, y, pageWidth, 35, 'F');
  y += 5;

  // Heading
  pdf.setTextColor(15, 23, 42); // slate-900
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('Financial Summary', margin, y);
  y += 7;

  // Cards layout
  const availableWidth = pageWidth - (margin * 2) - 6; // 3mm gaps between 3 cards
  const cardWidth = availableWidth / 3;
  const cardHeight = 18;
  const cardGap = 3;

  const cards = [
    { label: 'TOTAL SALES', value: `PKR ${formatCurrency(totalSales)}`, color: [34, 197, 94] as [number, number, number] },
    { label: 'INCOME', value: `PKR ${formatCurrency(incomeTotal)}`, color: [59, 130, 246] as [number, number, number] },
    { label: 'DONATION', value: `PKR ${formatCurrency(donationTotal)}`, color: [99, 102, 241] as [number, number, number] },
  ];

  cards.forEach((card, index) => {
    const cardX = margin + (index * (cardWidth + cardGap));
    // Card background
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(226, 232, 240); // slate-200
    pdf.setLineWidth(0.3);
    pdf.roundedRect(cardX, y, cardWidth, cardHeight, 2, 2, 'FD');

    // Label
    pdf.setTextColor(100, 116, 139); // slate-500
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.text(card.label, cardX + 3, y + 5);

    // Value
    pdf.setTextColor(card.color[0], card.color[1], card.color[2]);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(orientation === 'landscape' ? 13 : 11);
    pdf.text(card.value, cardX + 3, y + 13);
  });

  y += cardHeight + 8;

  // Table header
  const headers = ['Income Category', 'Revenue Type', 'Total Amount'];
  // Make table span full width between margins
  const tableAvailableWidth = pageWidth - (margin * 2);
  const colWidths = [
    Math.round(tableAvailableWidth * 0.6), // category
    Math.round(tableAvailableWidth * 0.2), // revenue type
    Math.round(tableAvailableWidth * 0.2), // amount
  ];
  const tableWidth = colWidths.reduce((a, b) => a + b, 0);
  pdf.setFillColor(241, 245, 249);
  pdf.rect(margin, y, tableWidth, 8, 'F');
  pdf.setTextColor(51, 65, 85);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  let x = margin;
  headers.forEach((h, i) => {
    const alignRight = i === 2; // total amount column
    if (alignRight) {
      pdf.text(h, x + colWidths[i] - 2, y + 5, { align: 'right' });
    } else {
      pdf.text(h, x + 2, y + 5);
    }
    x += colWidths[i];
  });
  y += 8;

  // Rows
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(7.5);
  const rowHeight = 7;
  data.forEach((r, idx) => {
    if (y > pageHeight - 30) {
      pdf.addPage();
      y = margin + 10;
    }
    if (idx % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, y, tableWidth, rowHeight, 'F');
    }
    let colX = margin;
    // Income Category
    pdf.text(String(r.income_category_name || ''), colX + 2, y + 5);
    colX += colWidths[0];
    // Revenue Type
    pdf.text(String(r.revenue_type || ''), colX + 2, y + 5);
    colX += colWidths[1];
    // Total Amount (right aligned)
    pdf.text(`PKR ${formatCurrency(r.total_amount || 0)}`, colX + colWidths[2] - 2, y + 5, { align: 'right' });
    y += rowHeight;
  });

  // Footer (consistent with sales report)
  const totalPages = pdf.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);

    // Footer background
    pdf.setFillColor(249, 250, 251); // slate-50
    pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');

    // Top border for footer
    pdf.setDrawColor(226, 232, 240); // slate-200
    pdf.setLineWidth(0.5);
    pdf.line(0, pageHeight - 15, pageWidth, pageHeight - 15);

    // Footer text
    pdf.setTextColor(100, 116, 139); // slate-500
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('This is a computer-generated document. No signature is required.', pageWidth / 2, pageHeight - 10, { align: 'center' });
    pdf.text(`Report Period Summary | Total Records: ${data.length}`, pageWidth / 2, pageHeight - 6, { align: 'center' });

    // Page number (bottom right)
    pdf.setTextColor(71, 85, 105); // slate-600
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 4, { align: 'right' });
  }

  pdf.save(filename);
}


